import { Autocomplete, Fade, Grid, TextField, Typography, useTheme } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { buildGenericSeriesStack, valueFormatter } from '../../../../shared/interfaces/analytics';
import { stackedRowTotals } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'; import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { GraphBarChartStacked } from '../graphs/graph-bar-chart-stacked';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { DrillDownDrawer } from '../drill-down-drawer';
import { useDrillDown } from '../use-drill-down';
import { useDrillDimensionLabel } from '../use-drill-dimension-label';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsBySubsidiarySkeleton } from './analytics-by-subsidiary-skeleton';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';

export const AnalyticsBySubsidiary: FunctionComponent<{
  reportPeriodId: string;
  emissionType: 'marketBased' | 'locationBased';
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, the breakdown donut carries the comparison period as an inner ring. */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
}> = ({ reportPeriodId, emissionType, setLoaded, comparisonPeriod, periodLabel }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const emissionService = new EmissionService();
  const subsidiaryService = SubsidiaryService.getInstance();
  // Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  // State
  const [pieSubsidiaryEmissions, setPieSubsidiaryEmissions] = useState<any>();
  const [comparisonPieSubsidiaryEmissions, setComparisonPieSubsidiaryEmissions] = useState<any>();
  const [stackedSubsidiaryEmissions, setStackedSubsidiaryEmissions] = useState<any>();
  const [comparisonStackedSubsidiaryEmissions, setComparisonStackedSubsidiaryEmissions] = useState<any>();
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string>('');
  const [allSubsidiaries, setAllSubsidiaries] = useState<ISubsidiary[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  // Variables
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  // Click-to-drill: a stale selection clears when the period, basis or comparison changes.
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
  const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriod?.id]);
  const dimLabel = useDrillDimensionLabel();
  // The stacked history keys its series by entity label only; the same-dimension donut carries the
  // ids, so resolve a clicked series label back to its subsidiary id through this map.
  const subsidiaryIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    (pieSubsidiaryEmissions?.[emissionType] || []).forEach((item: any) => {
      if (item?.id != null) map.set(String(item.label), String(item.id));
    });
    return map;
  }, [pieSubsidiaryEmissions, emissionType]);

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 5 : 3;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=subsidiary&topN=all`)
      .then((response) => {
        setPieSubsidiaryEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=subsidiary&topN=all`)
        .then((response) => {
          setComparisonPieSubsidiaryEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonPieSubsidiaryEmissions(undefined);
          checkLoadingStatus();
        });

      analyticsService
        .getStackedData(comparisonPeriod.id, `?data=subsidiary&topN=all`)
        .then((response) => {
          setComparisonStackedSubsidiaryEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonStackedSubsidiaryEmissions(undefined);
          checkLoadingStatus();
        });
    } else {
      setComparisonPieSubsidiaryEmissions(undefined);
      setComparisonStackedSubsidiaryEmissions(undefined);
    }

    analyticsService
      .getStackedData(requiredReportPeriodId, `?data=subsidiary&topN=all`)
      .then((response) => {
        setStackedSubsidiaryEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    emissionService
      .getByDomainHighest(
        requiredReportPeriodId,
        'subsidiary',
        0,
        perPage,
        orderBy,
        order,
      )
      .then((emissionsResponse) => {
        const subsidiaryId = emissionsResponse.rows[0]?.subsidiaryId ?? '';
        setSelectedSubsidiaryId(subsidiaryId);
        setEmissionRows(emissionsResponse.rows);
        setTotalRecords(emissionsResponse.count);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id]);

  useEffect(() => {
    subsidiaryService
      .getAll()
      .then(setAllSubsidiaries)
      .catch((err) => console.log(err));
  }, []);

  const getEmissionsForSubsidiary = (
    reportPeriodIdParam: string,
    subsidiaryId: string,
    page: number,
  ) => {
    if (!reportPeriodIdParam || !subsidiaryId) return;
    emissionService
      .getAll(
        reportPeriodIdParam,
        page,
        perPage,
        orderBy,
        order,
        `&subsidiaryId=${subsidiaryId}`,
      )
      .then((response) => {
        setEmissionRows(response.rows);
        setTotalRecords(response.count);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (requiredReportPeriodId) {
      getEmissionsForSubsidiary(requiredReportPeriodId, selectedSubsidiaryId, inputPage);
    }
  }, [inputPage, perPage, orderBy, order]);

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsBySubsidiarySkeleton />
          </div>
        </Fade>
      )}

      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {pieSubsidiaryEmissions && pieSubsidiaryEmissions[emissionType] && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    title={`GHG Emission Breakdown by ${screenLabelsContext.getLabel(
                      'subsidiary',
                      { plural: false, capitalize: 'first' },
                    )}`}
                    tooltip={`The greenhouse gas emission breakdown per ${screenLabelsContext.getLabel(
                      'subsidiary',
                      { plural: false },
                    )} for the selected report period`}
                    dataset={pieSubsidiaryEmissions[emissionType]}
                    colors={colors}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieSubsidiaryEmissions?.[emissionType]
                      ? { dataset: comparisonPieSubsidiaryEmissions[emissionType], label: comparisonPeriod.label }
                      : undefined}
                    onSelect={(item) => drill.openDrill({ dimension: 'subsidiary', id: item.id, label: item.label, value: item.value })}
                  />
                </Grid>
              )}

              {stackedSubsidiaryEmissions && stackedSubsidiaryEmissions[emissionType] && (
                <Grid
                  item
                  xl={8}
                  lg={8}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphBarChartStacked
                    enableTopN
                    defaultTopN={10}
                    title={`GHG Emission History for ${screenLabelsContext.getLabel(
                      'subsidiary',
                      { plural: true, capitalize: 'first' },
                    )}`}
                    tooltip={`The greenhouse gas emission history for each month in the selected report period per ${screenLabelsContext.getLabel(
                      'subsidiary',
                      { plural: false },
                    )}`}
                    dataset={stackedSubsidiaryEmissions[emissionType]}
                    series={buildGenericSeriesStack(
                      stackedSubsidiaryEmissions[emissionType],
                      valueFormatter,
                    )}
                    colors={colors}
                    comparison={comparisonPeriod && comparisonStackedSubsidiaryEmissions?.[emissionType]
                      ? {
                        totals: stackedRowTotals(comparisonStackedSubsidiaryEmissions[emissionType]),
                        label: comparisonPeriod.label,
                        valueFormatter,
                      }
                      : undefined}
                    onSelect={(seriesKey) => {
                      const id = subsidiaryIdByLabel.get(seriesKey);
                      if (id) drill.openDrill({ dimension: 'subsidiary', id, label: seriesKey });
                    }}
                  />
                </Grid>
              )}

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Grid container>
                  <Grid item lg={8} sm={12}>
                    <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                      <b>
                        Emission Records by{' '}
                        {screenLabelsContext.getLabel('subsidiary', {
                          plural: false,
                          capitalize: 'first',
                        })}
                      </b>
                    </Typography>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    {allSubsidiaries.length > 0 && (
                      <Autocomplete
                        id="subsidiary-filter-autocomplete"
                        options={[...allSubsidiaries].sort((a, b) =>
                          a.name.localeCompare(b.name),
                        )}
                        getOptionLabel={(option) => option.name ?? ''}
                        value={
                          allSubsidiaries.find((s) => s.id === selectedSubsidiaryId) ?? null
                        }
                        onChange={(_, value) => {
                          const id = value?.id ?? '';
                          setSelectedSubsidiaryId(id);
                          if (id !== selectedSubsidiaryId) setInputPage(0);
                          if (requiredReportPeriodId)
                            getEmissionsForSubsidiary(requiredReportPeriodId, id, 0);
                        }}
                        size="small"
                        sx={{ width: '100%', mb: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Filter"
                            variant="outlined"
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
                <EmissionsTable
                  emissions={emissionRows}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  setOrderBy={setOrderBy}
                  setOrder={setOrder}
                  backLink={'/analytics/dashboard?tab=emissions&dim=subsidiary'}
                  backText="Back to analytics"
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}

      <DrillDownDrawer
        open={drill.open}
        onClose={drill.close}
        selection={drill.selection}
        reportPeriodId={requiredReportPeriodId || ''}
        periodLabel={periodLabel}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonPeriod?.id}
        dimensionLabel={dimLabel(drill.selection?.dimension)}
      />
    </>
  );
};
